import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputDate from '@components/InputDate';
import InputFieldControlled from '@components/InputFieldControlled';
import InputGender from '@components/InputGender';
import InputPhone from '@components/InputPhone';
import InputPostal from '@components/InputPostal';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRO, UPDATE_BOSS_ADMINISTRATIVE_PRO } from '@gql/pros';
import { Grid } from '@mui/material';
import moment from 'moment';
import { useForm } from 'react-hook-form';

type Props = {
    pro?: any;
    onValidition?: (data?: any) => void;
    onSuccess: (data?: any) => void;
    onError: (error?: any) => void;
    showDescription?: boolean;
    disabled?: boolean;
    isEdit?: boolean;
    onBack?: () => void;
};

const OwnerForm = React.forwardRef((props: Props, ref) => {
    const pro = props.pro;

    const defaultValues = useMemo(
        () => ({
            paramNextStep: '',
            bossFirstname: pro?.bossFirstname,
            bossLastname: pro?.bossLastname,
            bossBirthday: pro?.bossBirthday,
            bossEmail: pro?.bossEmail,
            bossGender: pro?.bossGender,
            bossAddress: pro?.bossAddress,
            bossAddress2: pro?.bossAddress2,
            bossPhoneNumber: pro?.bossPhoneNumber,
            bossPostal: pro?.bossPostal,
            bossCity: pro?.bossCity,
            bossCountry: pro?.bossCountry,
            bossCountryCode: pro?.bossCountryCode,
        }),
        [pro],
    );

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        trigger,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [hide, setHide] = useState(false);
    const handleHide = hideState => {
        setHide(hideState);
    };

    const [update] = useMutation(UPDATE_BOSS_ADMINISTRATIVE_PRO, {
        onCompleted: () => {
            setLoading(false);
            enqueueSnackbar('Informations modifiées avec succès', {
                variant: 'success',
            });
        },
        onError: error => {
            enqueueSnackbar(error.message, {
                variant: 'error',
            });
            props.onError(error);
            setLoading(false);
        },
    });

    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const submitHandler = useCallback(
        async data => {
            if (loading) return;
            setLoading(true);
            const paramNextStep: any = data?.paramNextStep;
            delete data.paramNextStep;
            const request = {
                variables: {
                    proInput: {
                        ...data,
                        bossBirthday: moment.utc(data.bossBirthday).toISOString(),
                        // if no changes while updating, take the default value, the phoneNumber value sent from the Input of 'FR +33123123123' , while from the API it's split into its own item
                        bossCountryCode: data.bossPhoneNumber.split(' ')[1]
                            ? data.bossPhoneNumber.split(' ')[0]
                            : data.bossCountryCode,
                        bossPhoneNumber: data.bossPhoneNumber.split(' ')[1]
                            ? data.bossPhoneNumber.split(' ')[1]
                            : data.bossPhoneNumber,
                    },
                    proId: props.pro?._id,
                },
                refetchQueries: () => [
                    {
                        query: GET_PRO,
                        variables: { proId: props.pro?._id },
                    },
                ],
            };
            const result = await update(request);

            data.paramNextStep = paramNextStep;
            props.onSuccess(data);

            setLoading(false);
            setHide(false);

            if (!!result.errors) {
                props.onError(result.errors);
                return;
            }
            setFormState('view');
        },
        [loading, props, update],
    );

    // register trigger so it can triggered by parent component
    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            const isValid = await trigger();
            if (isValid && isDirty) {
                await handleSubmit(submitHandler)();
            }
            return isValid;
        },
    }));

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    return (
        <FormContainer
            onBack={props.onBack}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            hideEdit={props.disabled}
            onCancel={() => {
                setHide(false);
                reset();
            }}
            onSubmit={handleSubmit(submitHandler)}
            submitText={props.isEdit ? 'Enregistrer' : 'Suivant'}
            breadcrumbs={['Mes informations', 'Votre identité']}
            formTitle="Votre identité"
            formDescription={
                !props?.isEdit
                    ? "Veuillez vérifier les informations de votre établissement. En cas d'erreur ou de changement, vous pourrez nous envoyer une demande de modification ultérieurement."
                    : 'Veuillez vérifier les informations de votre établissement.'
            }>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <InputGender
                                    label="Genre"
                                    name="bossGender"
                                    viewOnly={formState === 'view'}
                                    control={control}
                                    defaultValue={defaultValues?.bossGender}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputDate
                                    viewOnly={formState === 'view'}
                                    label="Date de naissance"
                                    placeholder="Date de naissance"
                                    initialDate={
                                        defaultValues.bossBirthday
                                            ? moment(defaultValues.bossBirthday)
                                            : moment(new Date()).subtract(16, 'years')
                                    }
                                    maxDate={moment(new Date()).subtract(16, 'years')}
                                    rules={{
                                        validate: value => {
                                            if (new Date().getFullYear() - new Date(value).getFullYear() < 16) {
                                                return `You must be older than 16 years old`;
                                            }
                                            return null;
                                        },
                                    }}
                                    name="bossBirthday"
                                    defaultValue={defaultValues.bossBirthday}
                                    control={control}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossFirstname"
                                    control={control}
                                    label="Prénom du propriétaire"
                                    placeholder="Prénom du propriétaire"
                                    defaultValue={defaultValues?.bossFirstname}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossLastname"
                                    control={control}
                                    label="Nom du propriétaire"
                                    placeholder="Nom du propriétaire"
                                    defaultValue={defaultValues?.bossLastname}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossEmail"
                                    control={control}
                                    keyboardType="email-address"
                                    label="E-mail"
                                    placeholder="Email"
                                    defaultValue={defaultValues?.bossEmail}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputPhone
                                    label="Portable professionnel"
                                    viewOnly={formState === 'view'}
                                    // countryCode={defaultValues?.bossCountryCode}
                                    defaultValue={defaultValues?.bossPhoneNumber}
                                    name="bossPhoneNumber"
                                    hide={hide}
                                    handleHide={handleHide}
                                    control={control}
                                    placeholder="N° de téléphone"
                                    required
                                    inputMask={[/\d/, ' ', /\d/, /\d/, ' ', /\d/, /\d/, ' ', /\d/, /\d/, ' ', /\d/, /\d/]}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossAddress"
                                    control={control}
                                    label="Adresse"
                                    placeholder="Adresse"
                                    defaultValue={defaultValues?.bossAddress}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossAddress2"
                                    control={control}
                                    label="Adresse complémentaire"
                                    placeholder="Adresse complémentaire"
                                    defaultValue={defaultValues?.bossAddress2}
                                    required={false}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputPostal
                                    viewOnly={formState === 'view'}
                                    name="bossPostal"
                                    control={control}
                                    label="Code postal"
                                    placeholder="Code postal"
                                    defaultValue={defaultValues?.bossPostal}
                                    onSelect={function (city: string, postal: string): void {
                                        setValue('bossCity', city);
                                    }}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossCity"
                                    control={control}
                                    label="Ville"
                                    placeholder="Ville"
                                    defaultValue={defaultValues?.bossCity}
                                    disabled
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputFieldControlled
                                    viewOnly={formState === 'view'}
                                    name="bossCountry"
                                    control={control}
                                    label="Pays"
                                    placeholder="Pays"
                                    defaultValue={defaultValues?.bossCountry}
                                    required
                                />
                            </Grid>
                        </Grid>
        </FormContainer>
    );
});

export default OwnerForm;
